import { useState, useEffect, useCallback, useMemo } from 'react';

export type WeatherType = 'clear' | 'rain' | 'fog' | 'snow';

interface WeatherState {
  current: WeatherType;
  intensity: number; // 0-1
  transitionProgress: number;
}

const STORAGE_KEY = 'weather-system-data';

const getSeasonalWeather = (): WeatherType => {
  const month = new Date().getMonth();
  // Winter months (Dec, Jan, Feb) - snow more likely
  if (month === 11 || month === 0 || month === 1) {
    return Math.random() > 0.6 ? 'snow' : 'clear';
  }
  // Spring/Autumn (Mar, Apr, May, Sep, Oct, Nov) - rain more likely
  if ([2, 3, 4, 8, 9, 10].includes(month)) {
    return Math.random() > 0.7 ? 'rain' : 'clear';
  }
  // Summer - mostly clear, occasional fog in morning
  const hour = new Date().getHours();
  if (hour >= 5 && hour <= 8 && Math.random() > 0.8) {
    return 'fog';
  }
  return 'clear';
};

const getBehavioralWeather = (patterns: { rushBehavior?: number; silenceTime?: number }): WeatherType | null => {
  // Rushed behavior = fog (confusion)
  if (patterns.rushBehavior && patterns.rushBehavior > 80) {
    return 'fog';
  }
  // Lots of silence = rain (contemplative)
  if (patterns.silenceTime && patterns.silenceTime > 600) {
    return 'rain';
  }
  return null;
};

export const useWeatherSystem = () => {
  const [weather, setWeather] = useState<WeatherState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if weather should refresh (every 4 hours)
        if (Date.now() - parsed.lastUpdate < 4 * 60 * 60 * 1000) {
          return {
            current: parsed.current,
            intensity: parsed.intensity,
            transitionProgress: 1,
          };
        }
      }
    } catch {}
    return {
      current: getSeasonalWeather(),
      intensity: 0.5 + Math.random() * 0.5,
      transitionProgress: 0,
    };
  });

  useEffect(() => {
    // Animate transition in
    if (weather.transitionProgress < 1) {
      const timer = setTimeout(() => {
        setWeather(prev => ({
          ...prev,
          transitionProgress: Math.min(prev.transitionProgress + 0.05, 1),
        }));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [weather.transitionProgress]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        current: weather.current,
        intensity: weather.intensity,
        lastUpdate: Date.now(),
      }));
    } catch {}
  }, [weather.current, weather.intensity]);

  const setWeatherType = useCallback((type: WeatherType, intensity?: number) => {
    setWeather({
      current: type,
      intensity: intensity ?? 0.5 + Math.random() * 0.5,
      transitionProgress: 0,
    });
  }, []);

  const refreshWeather = useCallback((behaviorPatterns?: { rushBehavior?: number; silenceTime?: number }) => {
    const behavioralWeather = behaviorPatterns ? getBehavioralWeather(behaviorPatterns) : null;
    const newWeather = behavioralWeather ?? getSeasonalWeather();
    setWeatherType(newWeather);
  }, [setWeatherType]);

  const weatherStyles = useMemo(() => {
    const opacity = weather.intensity * weather.transitionProgress;
    
    switch (weather.current) {
      case 'rain':
        return {
          filter: `saturate(${0.8 - opacity * 0.2}) brightness(${0.95 - opacity * 0.1})`,
          transition: 'filter 2s ease-in-out',
        };
      case 'fog':
        return {
          filter: `blur(${opacity * 0.5}px) brightness(${1.05 + opacity * 0.1})`,
          transition: 'filter 3s ease-in-out',
        };
      case 'snow':
        return {
          filter: `brightness(${1.1 + opacity * 0.1}) contrast(${0.95 - opacity * 0.05})`,
          transition: 'filter 2s ease-in-out',
        };
      default:
        return {};
    }
  }, [weather]);

  return {
    weather: weather.current,
    intensity: weather.intensity,
    transitionProgress: weather.transitionProgress,
    weatherStyles,
    setWeatherType,
    refreshWeather,
    isActive: weather.current !== 'clear' && weather.transitionProgress > 0,
  };
};
